import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const BrandingContext = createContext(null);

/**
 * Tenant branding overrides the shadcn semantic tokens at runtime.
 * It writes inline CSS variables on <html> so it affects BOTH light and dark
 * themes (a tenant primary color stays consistent across themes).
 *
 * Tokens overridden:
 *   --primary, --ring, --chart-1 -> tenant primary
 *   --accent,  --chart-2         -> tenant accent
 *
 * Tokens NOT touched (keep theme defaults):
 *   --background, --foreground, --card, --muted, --border, --input, etc.
 */
const applyTheme = (primary, accent) => {
    const root = document.documentElement;
    if (primary) {
        root.style.setProperty("--primary", primary);
        root.style.setProperty("--ring", primary);
        root.style.setProperty("--chart-1", primary);
    } else {
        root.style.removeProperty("--primary");
        root.style.removeProperty("--ring");
        root.style.removeProperty("--chart-1");
    }
    if (accent) {
        root.style.setProperty("--accent", accent);
        root.style.setProperty("--chart-2", accent);
    } else {
        root.style.removeProperty("--accent");
        root.style.removeProperty("--chart-2");
    }
};

const resetTheme = () => applyTheme(null, null);

export const BrandingProvider = ({ children }) => {
    const { user } = useAuth();
    const [branding, setBranding] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!user) {
            setBranding(null);
            resetTheme();
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.get("/tenants/me/branding");
            setBranding(data);
        } catch {
            setBranding(null);
        } finally { setLoading(false); }
    }, [user]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (branding) {
            applyTheme(branding.primary_color, branding.accent_color);
        } else {
            resetTheme();
        }
        return () => resetTheme();
    }, [branding]);

    const refresh = useCallback(() => load(), [load]);

    const value = useMemo(
        () => ({ branding, loading, refresh, setBranding }),
        [branding, loading, refresh]
    );

    return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = () => {
    const ctx = useContext(BrandingContext);
    if (!ctx) throw new Error("useBranding must be used within BrandingProvider");
    return ctx;
};

// Default palettes — aligned with the new indigo + emerald base theme.
export const DEFAULT_PALETTE = [
    { name: "Indigo Base", primary: "244 75% 59%", accent: "160 84% 39%" },
    { name: "Esmeralda", primary: "160 84% 39%", accent: "200 92% 45%" },
    { name: "Cobalto", primary: "217 91% 60%", accent: "199 89% 48%" },
    { name: "Teal", primary: "174 84% 40%", accent: "200 92% 45%" },
    { name: "Violeta", primary: "262 83% 58%", accent: "291 64% 52%" },
    { name: "Ámbar", primary: "38 92% 55%", accent: "24 95% 58%" },
    { name: "Rosa", primary: "330 81% 60%", accent: "291 64% 52%" },
    { name: "Acero", primary: "215 25% 45%", accent: "200 30% 55%" },
];

// Utility: convert HEX to HSL string "H S% L%"
export const hexToHsl = (hex) => {
    if (!hex) return null;
    let h = hex.replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    if (h.length !== 6) return null;
    const r = parseInt(h.slice(0, 2), 16) / 255;
    const g = parseInt(h.slice(2, 4), 16) / 255;
    const b = parseInt(h.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let hh = 0, s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: hh = (g - b) / d + (g < b ? 6 : 0); break;
            case g: hh = (b - r) / d + 2; break;
            case b: hh = (r - g) / d + 4; break;
            default: hh = 0;
        }
        hh /= 6;
    }
    return `${Math.round(hh * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export const hslToHex = (hsl) => {
    if (!hsl) return "#4F46E5";
    const m = hsl.match(/^\s*(\d{1,3})\s+(\d{1,3})%\s+(\d{1,3})%\s*$/);
    if (!m) return "#4F46E5";
    const h = Number(m[1]) / 360;
    const s = Number(m[2]) / 100;
    const l = Number(m[3]) / 100;
    const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    let r, g, b;
    if (s === 0) {
        r = g = b = l;
    } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }
    const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};
